import { Box, Button, FormControl, FormLabel, Select } from '@chakra-ui/react'
import type { PoleasQuery } from 'BrokerTsLib/src/dtos'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { useContext } from 'react'

import { InputGroup } from '@/components/InputGroup'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { SearchWrapper } from '@/containers/search/SearchWrapper'
import { useAuth } from '@/context/AuthContext'
import { usePartner } from '@/context/PartnerContext'
import { useInsurers } from '@/hooks'
import { useGetSearchNames, useGetSearchStates } from '@/hooks/usePoleas'
import { Search } from '@/icons'
import { poleasContext } from '@/pages/poleas/PoleasImportsPage'
import { SelectFormControl } from 'BrokerTsLib/src/components'

const pagings = [{ val: 10, desc: '10' }, { val: 20, desc: '20' }, { val: 50, desc: '50' }, { val: 100, desc: '100' }, { val: 0, desc: 'vše' }]

interface PoleasSearchFormProps {
  onSubmit: (search: PoleasQuery) => void,
  filter: PoleasQuery,
}

export const PoleasSearchForm = ({ onSubmit, filter }: PoleasSearchFormProps) => {
  const { setCurrentPage, setPageSize, loading: loadingList } = useContext(poleasContext)

  const methods = useForm<PoleasQuery>({
    defaultValues: filter,
  })

  const { register, getValues, setValue, handleSubmit, formState: { isSubmitting }, control } = methods

  const { data: allInsurers, isLoading: isLoadingInsurers } = useInsurers(null, false)
  const { data: imports, isLoading: isLoadingImports } = useGetSearchNames()
  const { data: states, isLoading: isLoadingStates } = useGetSearchStates()

  const { user } = useAuth()
  const { currentPartner } = usePartner()

  const onFormSubmit = (filter: PoleasQuery) => {
    const pagesize = Number(filter.pageSize)
    setPageSize(pagesize)
    setCurrentPage(1)
    return onSubmit(filter)
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <SearchWrapper>
          <SelectFormControl name='importId' control={control}
            options={imports?.map(x => ({
              value: x.id.toString(),
              label: x.name
            }))}
            onChange={e => {
              setValue('pageSize', e as number ? 0 : 10)
              setValue('showAllItems', !!e)
            }}
            label="Název importu"
            unselectedText='Vyberte'
            isLoading={isLoadingImports}
          />

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="offerNumber">Číslo nabídky</FormLabel>

            <InputGroup id="offerNumber" placeholder="Např: 73622" {...register('insuranceParamsId')} />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="offerNumber">Číslo leas. smlouvy</FormLabel>

            <InputGroup {...register('leasingNumber')} placeholder='Např: 1234567890' />
          </FormControl>

          <SelectFormControl name='offerState' control={control}
            options={states?.map(x => ({
              value: x.id.toString(),
              label: x.name
            }))}
            label="Stav nabídky"
            isLoading={isLoadingStates}
            unselectedText='Všechny'
          />

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="dateImportedFrom">Importováno od</FormLabel>

            <Datepicker name='dateImportedFrom' />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="dateImportedTo">Importováno do</FormLabel>

            <Datepicker name='dateImportedTo' />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="dateAccessFrom">Přístup uživatele od</FormLabel>

            <Datepicker name='dateAccessFrom' />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="dateAccessTo">Přístup uživatele do</FormLabel>

            <Datepicker name='dateAccessTo' />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="dateInsuranceStartFrom">Počátek pojištění od</FormLabel>

            <Datepicker name='dateInsuranceStartFrom' />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="dateInsuranceStartTo">Počátek pojištění do</FormLabel>

            <Datepicker name='dateInsuranceStartTo' />
          </FormControl>

          <SelectFormControl name='insurerId' control={control}
            options={allInsurers?.data?.map(x => ({
              value: x.id.toString(),
              label: x.description ?? ''  
            }))}
            label="Pojišťovna"
            isLoading={isLoadingInsurers}
            unselectedText='Vyberte'
          />

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="spz">Registrační značka</FormLabel>

            <InputGroup id="spz" placeholder="Např: 1A1 0000" {...register('registrationMark')} />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="vin">VIN</FormLabel>

            <InputGroup id="vin" placeholder="Např: 2HNYD188068515943" {...register('vin')} />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="insureeName">Pojistník (příjmení/název firmy)</FormLabel>

            <InputGroup id="insureeName" placeholder="Např: Novák" {...register('legalPersonName')} />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="insureeNumber">Pojistník (rodné číslo/IČO)</FormLabel>

            <InputGroup
              id="insureeNumber"
              placeholder="Např: 9001012233"
              {...register('legalPersonIdentification')}
            />
          </FormControl>

          {user?.isSupervisor && currentPartner && currentPartner.partner! == 'Kapitol' &&
              <>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="userCity">Obec uživatele</FormLabel>

                  <InputGroup id="userCity" {...register('userCity')} />
                </FormControl>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="userStreet">Ulice uživatele</FormLabel>

                  <InputGroup id="userStreet"  {...register('userStreet')} />
                </FormControl>
              </>
          }

          <FormControl variant=""
            sx={{
              gridColumn: { base: '', md: '2 / 3' }
            }}
          >
            <FormLabel htmlFor="insureeNumber">Počet záznamů na stránce</FormLabel>

            <Select {...register('pageSize')}
              onChange={e => setValue('showAllItems', e.target.value == '0')}
            >
              {pagings.map(x => (
                <option key={x.val} value={x.val.toString()}>
                  {x.desc}
                </option>
              )
              )}
            </Select>


          </FormControl>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'end',
              flexWrap: 'wrap',
              gridColumn: { base: '', md: '3 / 4' },
            }}
          >

            <Button type="submit" colorScheme="primary" isLoading={isSubmitting || loadingList}>
              <Search />
              Vyhledat
            </Button>
          </Box>

        </SearchWrapper>
      </form>
    </FormProvider>
  )
}


